/**
 * GlobalErrorService — centralized error capture and reporting.
 *
 * Captures errors from: React render errors, ErrorBoundary,
 * repository errors, service errors, auth errors, RPC failures,
 * and network failures. Reports to the logger and exposes
 * an error subscription mechanism for UI feedback.
 */

import { LoggerFactory } from '@/lib/logging';
import type { LogContext } from '@/lib/logging';

export interface ErrorReport {
  id: string;
  timestamp: string;
  message: string;
  stack?: string;
  source: ErrorSource;
  context: LogContext;
}

export type ErrorSource =
  | 'react_render'
  | 'error_boundary'
  | 'repository'
  | 'service'
  | 'auth'
  | 'rpc'
  | 'network'
  | 'unknown';

type ErrorListener = (report: ErrorReport) => void;

class GlobalErrorServiceImpl {
  private listeners = new Set<ErrorListener>();
  private logger = LoggerFactory.create();

  capture(error: unknown, source: ErrorSource, context?: LogContext): ErrorReport {
    const message = error instanceof Error ? error.message : String(error);
    const stack = error instanceof Error ? error.stack : undefined;

    const report: ErrorReport = {
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      message,
      stack,
      source,
      context: context ?? {},
    };

    this.logger.error(`[${source}] ${message}`, {
      ...report.context,
      errorId: report.id,
      stack: report.stack,
    });

    this.listeners.forEach((listener) => {
      try {
        listener(report);
      } catch {
        // listener errors must not propagate
      }
    });

    return report;
  }

  captureFromErrorBoundary(error: Error, errorInfo: { componentStack?: string | null }): ErrorReport {
    return this.capture(error, 'error_boundary', {
      componentStack: errorInfo.componentStack ?? undefined,
    });
  }

  subscribe(listener: ErrorListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

export const globalErrorService = new GlobalErrorServiceImpl();
